// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react'
import { Download, Upload, Loader2, AlertTriangle, CheckCircle } from 'lucide-react'
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component'
import { JobPoller } from '@umd-dit-ai-workspace/jobs-component'
import {
  adminApi,
  type ExportResult,
  type ImportPreview,
  type ImportResult,
} from '../api/admin'

export function WorkspaceTransferView() {
  const { selectedOrganizationId, selectedWorkspaceId, userId } = useAuth() as {
    selectedOrganizationId?: string
    selectedWorkspaceId?: string
    userId?: string
  }

  // ── Export ────────────────────────────────────────────────────────────────
  const [orgId, setOrgId] = useState(selectedOrganizationId || '')
  const [wsId, setWsId] = useState(selectedWorkspaceId || '')
  const [exportJobId, setExportJobId] = useState<string | null>(null)
  const [exportId, setExportId] = useState<string | null>(null)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportResult, setExportResult] = useState<ExportResult | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const handleExport = async () => {
    if (!orgId.trim() || !wsId.trim() || !userId) return
    setExportBusy(true); setExportError(null); setExportResult(null); setExportJobId(null)
    try {
      const res = await adminApi.exportWorkspace(orgId.trim(), wsId.trim(), userId)
      setExportId(res.export_id)
      setExportJobId(res.job_id)
    } catch (e: any) {
      setExportError(e?.response?.data?.detail || e?.message || 'Failed to start export.')
      setExportBusy(false)
    }
  }

  const handleExportComplete = async () => {
    setExportJobId(null)
    try {
      const res = await adminApi.getExportResult(exportId!)
      if (res.status === 'failed') setExportError(res.error || 'Export failed.')
      else setExportResult(res)
    } catch (e: any) {
      setExportError(e?.message || 'Failed to read export result.')
    } finally {
      setExportBusy(false)
    }
  }

  // ── Import ────────────────────────────────────────────────────────────────
  const [bundleFile, setBundleFile] = useState<File | null>(null)
  const [bundleKey, setBundleKey] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [importJobId, setImportJobId] = useState<string | null>(null)
  const [importId, setImportId] = useState<string | null>(null)
  const [importBusy, setImportBusy] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const handleChooseBundle = async (file: File) => {
    setBundleFile(file); setBundleKey(null); setPreview(null); setImportResult(null)
    setImportError(null); setImportJobId(null); setImportBusy(true)
    try {
      const { upload_url, bundle_key } = await adminApi.requestImportUploadUrl(file.name)
      await adminApi.uploadBundle(upload_url, file)
      setBundleKey(bundle_key)
      setPreview(await adminApi.previewImport(bundle_key))
    } catch (e: any) {
      setImportError(e?.response?.data?.detail || e?.message || 'Failed to read bundle.')
    } finally {
      setImportBusy(false)
    }
  }

  const handleConfirmImport = async () => {
    if (!bundleKey || !userId || !selectedOrganizationId || !selectedWorkspaceId) return
    setImportBusy(true); setImportError(null); setImportResult(null)
    try {
      const res = await adminApi.importWorkspace(bundleKey, selectedOrganizationId, selectedWorkspaceId, userId)
      setImportId(res.import_id)
      setImportJobId(res.job_id)
    } catch (e: any) {
      setImportError(e?.response?.data?.detail || e?.message || 'Failed to start import.')
      setImportBusy(false)
    }
  }

  const handleImportComplete = async () => {
    setImportJobId(null)
    try {
      const res = await adminApi.getImportResult(importId!)
      if (res.status === 'failed') setImportError(res.error || 'Import failed.')
      else setImportResult(res)
    } catch (e: any) {
      setImportError(e?.message || 'Failed to read import result.')
    } finally {
      setImportBusy(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Job pollers */}
      {exportJobId && (
        <JobPoller
          jobId={exportJobId}
          jobContext={{ org_id: orgId || undefined, ws_id: wsId || undefined }}
          onStatusChange={() => {}}
          onComplete={handleExportComplete}
          onError={() => { setExportError('Export job failed.'); setExportBusy(false); setExportJobId(null) }}
        />
      )}
      {importJobId && (
        <JobPoller
          jobId={importJobId}
          jobContext={{ org_id: selectedOrganizationId || undefined, ws_id: selectedWorkspaceId || undefined }}
          onStatusChange={() => {}}
          onComplete={handleImportComplete}
          onError={() => { setImportError('Import job failed.'); setImportBusy(false); setImportJobId(null) }}
        />
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Move an entire workspace (documents, analyses, and their files) between environments.
          Export produces a bundle you download; import writes that bundle into the current
          environment. Import <strong>overwrites</strong> items with the same ids.
        </span>
      </div>

      {/* ── Export ─────────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Download className="h-5 w-5 text-blue-600" /> Export workspace
        </h3>
        <p className="text-sm text-gray-500 mb-4">Download this environment’s workspace as a bundle.</p>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Org ID</label>
            <input value={orgId} onChange={e => setOrgId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Workspace ID</label>
            <input value={wsId} onChange={e => setWsId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button
            onClick={handleExport}
            disabled={exportBusy || !orgId.trim() || !wsId.trim()}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
          >
            {exportBusy ? (<><Loader2 className="animate-spin h-4 w-4" /> Building bundle…</>) : (<>Export</>)}
          </button>
        </div>

        {exportError && <p className="mt-3 text-sm text-red-600">{exportError}</p>}
        {exportResult?.status === 'completed' && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 flex items-center justify-between gap-4">
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Bundle ready — {exportResult.dynamo_count} records, {exportResult.s3_count} files
              {typeof exportResult.blob_count === 'number' && exportResult.blob_count !== exportResult.s3_count
                ? ` (${exportResult.blob_count} unique after dedup)` : ''}.
            </span>
            {exportResult.download_url && (
              <a
                href={exportResult.download_url}
                className="shrink-0 bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 text-xs font-medium"
              >
                Download {exportResult.filename}
              </a>
            )}
          </div>
        )}
      </section>

      {/* ── Import ─────────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Upload className="h-5 w-5 text-blue-600" /> Import workspace
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Upload a bundle exported from another environment. You’ll see a preview before anything is written.
        </p>

        <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm text-gray-700">
          <Upload className="h-4 w-4 text-gray-500" />
          {bundleFile ? bundleFile.name : 'Choose bundle (.zip)…'}
          <input
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleChooseBundle(f) }}
          />
        </label>

        {importBusy && !preview && !importResult && !importJobId && (
          <p className="mt-3 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="animate-spin h-4 w-4" /> Uploading and reading bundle…
          </p>
        )}
        {importJobId && (
          <p className="mt-3 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="animate-spin h-4 w-4" /> Importing…
          </p>
        )}
        {importError && <p className="mt-3 text-sm text-red-600">{importError}</p>}

        {preview && !importResult && !importJobId && (
          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
            <p className="font-medium mb-1">Preview</p>
            <p>Workspace: <strong>{preview.org_id}/{preview.ws_id}</strong></p>
            <p>{preview.dynamo_count ?? '?'} DynamoDB records · {preview.s3_count ?? '?'} S3 files will be written into this environment.</p>
            <button
              onClick={handleConfirmImport}
              disabled={importBusy}
              className="mt-3 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium transition-colors flex items-center gap-2"
            >
              Confirm import (overwrite)
            </button>
          </div>
        )}

        {importResult?.status === 'completed' && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Imported — {importResult.dynamo_written} records written
            {importResult.dynamo_skipped ? `, ${importResult.dynamo_skipped} skipped` : ''}
            , {importResult.s3_total ?? importResult.s3_uploaded} files
            {typeof importResult.s3_copied === 'number' && importResult.s3_copied > 0
              ? ` (${importResult.s3_uploaded} unique + ${importResult.s3_copied} deduped copies)` : ''}.
          </div>
        )}
      </section>
    </div>
  )
}
